/**
 * Sniper — reading the engine's notification `reason` on a cover / VAR frame.
 *
 * Since PRED-159 the engine says what a SELL is in the reason rather than in a
 * type of its own:
 *
 *     sell_stage:{risk|review|reverted|base}   — which event priced the cover
 *     cover:{kickoff|period_end|fallback_no_kickoff_coverage|
 *            pre_goal_price_unavailable|chased|none_configured|kickoff_moot|ftts_reanchor}  — why the cover moved / appeared (or did not)
 *     snapshot_discarded                        — an old-format mark was dropped at boot
 *
 * Read by TYPE alone, those frames lie: a latch alert on the alarm type would
 * ring "goal reversed", and a cover chased a cent up the book would read as a
 * fresh placement. So every surface asks this file.
 *
 * The LATCH tokens of PRED-137 are unchanged and still ride `var_policy:` —
 * the ambiguous-create freeze (`…:sell_unconfirmed`, `…:frozen_unconfirmed`),
 * its resolution (`var_policy:unconfirmed:sell:{result}`), the operator release
 * and the placement invariant. The NO-buy and rebuy stages of that policy are
 * gone with it: a `no` / `rebuy` kind is not a thing this build renders.
 *
 * 🚨 Fail-safe direction: anything not POSITIVELY recognised as an execution is
 * an ALERT with no side. An unknown stage, cause or result literal is never
 * rendered as an order.
 */

export const VAR_POLICY_REASON_PREFIX = 'var_policy:';
export const SELL_STAGE_REASON_PREFIX = 'sell_stage:';
export const COVER_REASON_PREFIX = 'cover:';
/** An old-format ladder snapshot was dropped at boot; the mark was NOT re-adopted. */
export const SNAPSHOT_DISCARDED_REASON = 'snapshot_discarded';
/** A warm reload of the row config could not be read: the prior config stays live, the engine retries. */
export const CONFIG_RELOAD_DEFERRED_REASON = 'config_reload_deferred';

export interface VarPolicyReason {
  stage: string;
  cause: string;
  result: string;
}

/** The three fields of a `var_policy:` (latch) reason, or `null` for any other reason. */
export function parseVarPolicyReason(
  reason: string | null | undefined,
): VarPolicyReason | null {
  if (typeof reason !== 'string' || !reason.startsWith(VAR_POLICY_REASON_PREFIX)) {
    return null;
  }
  const parts = reason.slice(VAR_POLICY_REASON_PREFIX.length).split(':');
  // Stage first, result LAST: a cause carrying a `:` of its own must not shift
  // the result into it. Fewer than three fields = no result we can vouch for.
  if (parts.length < 3) return { stage: parts[0] ?? '', cause: '', result: '' };
  return {
    stage: parts[0],
    cause: parts.slice(1, -1).join(':'),
    result: parts[parts.length - 1],
  };
}

/**
 * The sell stages, VERBATIM from the engine. `reverted` is the ONLY one that
 * means the goal is gone; `base` is the cover going back to the row's own sell
 * once a review ends with the goal standing.
 */
export type SniperSellStage = 'risk' | 'review' | 'reverted' | 'base';
export const SNIPER_SELL_STAGES: readonly SniperSellStage[] = [
  'risk',
  'review',
  'reverted',
  'base',
];

/** The stage of a `sell_stage:` reason — the literal as sent, or `null` for any other reason. */
export function parseSellStageReason(reason: string | null | undefined): string | null {
  if (typeof reason !== 'string' || !reason.startsWith(SELL_STAGE_REASON_PREFIX)) {
    return null;
  }
  return reason.slice(SELL_STAGE_REASON_PREFIX.length);
}

/** The cause of a `cover:` reason — the literal as sent, or `null` for any other reason. */
export function parseCoverReason(reason: string | null | undefined): string | null {
  if (typeof reason !== 'string' || !reason.startsWith(COVER_REASON_PREFIX)) {
    return null;
  }
  return reason.slice(COVER_REASON_PREFIX.length);
}

/**
 * The frame types a cover rides on. The engine emits the cover's own life on
 * the three ladder-sell types and ONE `sniper_var_reversal` per annulment (the
 * alarm, `sell_stage:reverted`); that alarm type also carries the latch
 * (`var_policy:*`), the engine's `fire_unconfirmed:*` alert and, on frames from
 * before PRED-193, `annulment_unhandled:*`. Only `sell_stage:reverted` — or a legacy frame with no readable
 * reason — means the goal is gone. A `sell_stage:` / `cover:` reason on any
 * other type is not a cover this build knows and lands on the alert.
 */
const COVER_TYPES: ReadonlySet<string> = new Set([
  'sniper_var_reversal',
  'sniper_ladder_sell_placed',
  'sniper_ladder_sell_amended',
  'sniper_ladder_sell_filled',
]);
const COVER_FILLED_TYPE = 'sniper_ladder_sell_filled';
const COVER_AMENDED_TYPE = 'sniper_ladder_sell_amended';

const SELL_STAGE_LABEL: Record<SniperSellStage, string> = {
  risk: 'VAR risk sell',
  review: 'VAR review sell',
  reverted: 'goal reverted — sell',
  base: 'base sell restored',
};

const COVER_CAUSE_LABEL: Record<string, string> = {
  kickoff: 'cover placed on the kick-off',
  period_end: 'cover placed at the period end',
  fallback_no_kickoff_coverage: 'no kick-off coverage — cover placed relative to the buy',
  chased: 'cover chased the ask upward',
  kickoff_moot: 'goal reverted with the kick-off gate shut — base cover placed now (the kick-off is moot)',
  ftts_reanchor: 'first goal annulled and the new one buys the same side — position kept, base cover restored',
};
/** The one cover cause that is also a warning: the pre-goal ask was never captured. */
const COVER_PRE_GOAL_PRICE_UNAVAILABLE = 'pre_goal_price_unavailable';
/**
 * A reverted goal on a row with `goalReverted` off and no sell configured: the
 * position is HELD on a goal that no longer exists. Nothing was placed.
 */
const COVER_NONE_CONFIGURED = 'none_configured';

/**
 * Results where the venue never CONFIRMED the order: it may or may not be
 * resting. An alert whose label has to say so — "failed" would be a guess.
 */
const UNCONFIRMED_SUFFIX = '_unconfirmed';
const CHECK_VENUE = ' — CHECK THE VENUE: the order may have landed';

/**
 * `{stage}:{cause}:cover_unconfirmed` is NOT a latch: it is the reconcile of
 * the cover that could not be confirmed, and its `orderId` is the ENTRY
 * order's venue id. So it gets neither "client order id" nor "the order may
 * have landed", and it says nothing about the frozen state.
 */
export const COVER_UNCONFIRMED = 'cover_unconfirmed';

/**
 * The venue DEFINITIVELY rejected the sell (insufficient balance, market
 * closed, 400 / 401 / 403 / 422, 429 …). Unlike `*_unconfirmed` there is no
 * doubt about its fate: nothing was placed, and the engine retries on its
 * normal schedule.
 */
const SELL_REJECTED = 'sell_rejected';

/**
 * The mark is FROZEN: a sell of unknown fate, and until it resolves nothing
 * automatic acts on this market. Two shapes carry it —
 * `var_policy:{stage}:{cause}:frozen_unconfirmed` (a signal that could not act)
 * and `var_policy:frozen:{action}:frozen_unconfirmed` (a non-policy action that
 * was held back: cover, …). Always on `sniper_var_reversal`.
 */
const FROZEN_RESULT = 'frozen_unconfirmed';
const STAGE_FROZEN = 'frozen';

/**
 * The latch RESOLVING (or failing to): `var_policy:unconfirmed:sell:{result}`,
 * on `sniper_var_reversal`. The results, verbatim from the engine:
 *   - `resolved_landed` — the order is on the venue: resting, or terminal with
 *     a fill. `orderId` = the venue id, `filled` = what EXECUTED,
 *     `sizeRequested` = the order size. An execution only when `filled > 0`;
 *     with `filled == 0` it is an order confirmed RESTING, not a fill.
 *   - `resolved_cancelled_unfilled` — it existed and was cancelled unfilled.
 *   - `resolved_cancelled_on_halt` — found resting under a halt and cancelled;
 *     `filled` is whatever traded before the cancel.
 *   - `released_by_operator` / `release_refused_*` / `release_ignored_stale_coid`
 *     — the engine's answer to an operator's release.
 *   - `resolved_conflict` — a SECOND order of that kind was found and cancelled;
 *     `filled` is what traded before the cancel.
 *   - `still_unconfirmed` / `still_unconfirmed_read_failed` — the 5-minute
 *     reminder that the mark is still frozen (`orderId` = OUR client id).
 */
const STAGE_UNCONFIRMED = 'unconfirmed';
const RESOLVED_LANDED = 'resolved_landed';
const RESOLVED_CANCELLED_UNFILLED = 'resolved_cancelled_unfilled';
const RESOLVED_CANCELLED_ON_HALT = 'resolved_cancelled_on_halt';
const RESOLVED_CONFLICT = 'resolved_conflict';
const STILL_UNCONFIRMED = 'still_unconfirmed';
const STILL_UNCONFIRMED_READ_FAILED = 'still_unconfirmed_read_failed';
// An order of ours is resting, has to come off, and its cancel did not confirm.
// First sighting (`orderId` = the VENUE id) and its reminder (`orderId` = OUR
// client id — the engine emits them from different places).
const HALT_CANCEL_PENDING = 'halt_cancel_pending';
const CONFLICT_CANCEL_PENDING = 'conflict_cancel_pending';
const STILL_CANCEL_PENDING = 'still_cancel_pending';
// The engine NEVER concludes "did not land" on its own: a frozen mark stays
// frozen until the order is found or an OPERATOR releases it. These are the
// engine's answers to a release. `orderId` is OUR client id on all of them
// EXCEPT `release_refused_order_found`, which carries the venue's id — so an
// answer is matched to its row by fixture + market, never by that id alone.
export const RELEASED_BY_OPERATOR = 'released_by_operator';
const RELEASE_REFUSED_ORDER_FOUND = 'release_refused_order_found';
const RELEASE_REFUSED_READ_FAILED = 'release_refused_read_failed';
const RELEASE_REFUSED_HALTED = 'release_refused_halted';
const RELEASE_IGNORED_STALE_COID = 'release_ignored_stale_coid';
// The release GATE refusing on the engine side, and a command it could not parse.
const RELEASE_REFUSED_TOO_SOON = 'release_refused_too_soon';
const RELEASE_REFUSED_NO_CLEAN_READ = 'release_refused_no_clean_read';
const RELEASE_IGNORED_MALFORMED = 'release_ignored_malformed';
/** Every answer to a release starts with one of these. */
export function isReleaseOutcome(result: string): boolean {
  return result === RELEASED_BY_OPERATOR || result.startsWith('release_');
}
// The fixture was deactivated while an order's fate was unknown (`orderId` =
// OUR client id).
const FIXTURE_REMOVED = 'fixture_removed';

/**
 * The placement INVARIANT refusing an order: it would exceed the position.
 * Two shapes — `var_policy:invariant:{lane}:invariant_refused` (`orderId` = the
 * order it would have collided with) and `invariant_refused` as the result of
 * a normal stage (`orderId` = the id of the order that was NEVER sent, so it is
 * not shown).
 */
const INVARIANT_REFUSED = 'invariant_refused';
const STAGE_INVARIANT = 'invariant';
/** The only order kind a latch can hold since PRED-159: the cover sell. */
const LATCHED_KIND_SELL = 'sell';

export type VarPolicyOpKind = 'execution' | 'info' | 'alert';

export interface VarPolicyOpReading {
  stage: string;
  result: string;
  /**
   * `execution` — an order exists; `info` — nothing was placed because there
   * was nothing to do; `alert` — fail-closed, or a literal this build does not
   * know. The DEFAULT is `alert`: the engine is still adding results, and an
   * unknown one must never read as an order.
   */
  kind: VarPolicyOpKind;
  /** The order's side, or `null` when the frame placed NOTHING. */
  side: 'buy' | 'sell' | null;
  /** `cover · VAR risk sell` | `cover · chased the ask upward` | `Sniper latch · alert: …`. */
  label: string;
  alert: boolean;
  /** Whether `filled` / `avgPrice` on the frame are an executed quantity and price. */
  executed: boolean;
  /** Whether the frame REPRICED an order already resting rather than posting one. */
  amended: boolean;
  /** Whether the goal is actually GONE — the only stage the reversal cue rings for. */
  goalReversed: boolean;
  /**
   * What the frame's `orderId` IS, when it is worth showing: OUR client order
   * id on an unconfirmed / frozen frame (the handle to search the venue with),
   * the venue's own id once the order is confirmed. `null` = not shown.
   */
  orderIdKind: VarPolicyOrderIdKind;
  /**
   * The latch resolved to an order that IS on the venue. With nothing executed
   * it is an order confirmed RESTING — neither a fill nor a fresh placement.
   */
  landed: boolean;
  /**
   * How to word a POSITIVE `filled` on a frame that is not an execution of its
   * own — what traded before a cancel, what an abandoned order banked, what a
   * resting order has done so far. `null` = `filled` is not shown.
   */
  filledNote: string | null;
}

/**
 * `client` — OUR client order id (the handle to search the venue with);
 * `venue` — the venue's own id; `collision` — the order a refused placement
 * would have collided with.
 */
export type VarPolicyOrderIdKind = 'client' | 'venue' | 'collision' | null;

const LATCH_LABEL_PREFIX = 'Sniper latch · ';
const COVER_LABEL_PREFIX = 'cover · ';

/**
 * What a sniper op frame IS, when its reason is a cover / sell-stage / latch
 * reason — or `null` when it is not one (a legacy reason, or a plain frame
 * whose type says everything).
 */
export function varPolicyOpReading(
  type: string,
  reason: string | null | undefined,
): VarPolicyOpReading | null {
  if (reason === CONFIG_RELOAD_DEFERRED_REASON) {
    return {
      stage: CONFIG_RELOAD_DEFERRED_REASON,
      result: CONFIG_RELOAD_DEFERRED_REASON,
      kind: 'alert',
      side: null,
      label: `${LATCH_LABEL_PREFIX}alert: the engine could not read this row's new config — the PREVIOUS config is still live; it retries every 15 s`,
      alert: true,
      executed: false,
      amended: false,
      goalReversed: false,
      orderIdKind: null,
      landed: false,
      filledNote: null,
    };
  }
  if (
    reason === SNAPSHOT_DISCARDED_REASON ||
    reason?.startsWith(`${SNAPSHOT_DISCARDED_REASON}:`)
  ) {
    // `snapshot_discarded[:cover=<orderId>]:cancelled|left_resting` — the
    // engine cancels the discarded mark's buy and cover at boot; `left_resting`
    // means a cancel could not be confirmed and the row stays inert.
    const suffix = reason.slice(SNAPSHOT_DISCARDED_REASON.length + 1);
    const parts = suffix === '' ? [] : suffix.split(':');
    const cover = parts[0]?.startsWith('cover=') ? parts[0].slice('cover='.length) : null;
    const outcome = parts[parts.length - 1];
    const leftResting = outcome === 'left_resting';
    const orders = cover ? `its buy and its cover sell ${cover}` : 'its orders';
    const label = leftResting
      ? `${LATCH_LABEL_PREFIX}alert: an old-format mark was DISCARDED at boot and a cancel could NOT be confirmed — ${orders} may still be RESTING on the venue, check it; the row stays inert`
      : outcome === 'cancelled'
        ? `${LATCH_LABEL_PREFIX}alert: an old-format mark was DISCARDED at boot and NOT re-adopted — ${orders} were cancelled`
        : cover
          ? `${LATCH_LABEL_PREFIX}alert: an old-format mark was DISCARDED at boot and NOT re-adopted — its cover sell ${cover} may still be RESTING on the venue, check it`
          : `${LATCH_LABEL_PREFIX}alert: an old-format mark was DISCARDED at boot and NOT re-adopted — check the venue for a resting order`;
    return {
      stage: SNAPSHOT_DISCARDED_REASON,
      result: suffix || SNAPSHOT_DISCARDED_REASON,
      kind: 'alert',
      side: null,
      label,
      alert: true,
      executed: false,
      amended: false,
      goalReversed: false,
      orderIdKind: null,
      landed: false,
      filledNote: null,
    };
  }

  const sellStage = parseSellStageReason(reason);
  if (sellStage !== null) return sellStageReading(type, sellStage);
  const coverCause = parseCoverReason(reason);
  if (coverCause !== null) return coverReading(type, coverCause);

  if (type !== 'sniper_var_reversal') return null;
  const parsed = parseVarPolicyReason(reason);
  if (!parsed) return null;
  return latchReading(parsed);
}

/** A cover reading: an execution on the sell side, on a type the cover rides. */
function cover(
  type: string,
  stage: string,
  result: string,
  kind: VarPolicyOpKind,
  label: string,
  goalReversed = false,
): VarPolicyOpReading {
  const execution = kind !== 'alert' && COVER_TYPES.has(type);
  return {
    stage,
    result,
    kind: execution ? 'execution' : 'alert',
    // An alert places nothing: a side on it would draw an unknown literal as a
    // resting SELL in the ops feed. `pre_goal_price_unavailable` is the one
    // alert that DID sell, and sets its side itself.
    side: execution ? 'sell' : null,
    label: execution ? `${COVER_LABEL_PREFIX}${label}` : `${LATCH_LABEL_PREFIX}alert: ${label}`,
    alert: !execution,
    // `filled` is the executed quantity only on the FILL frame; a placement or
    // an amend reports the order size there.
    executed: execution && type === COVER_FILLED_TYPE,
    amended: execution && type === COVER_AMENDED_TYPE,
    goalReversed,
    orderIdKind: null,
    landed: false,
    filledNote: null,
  };
}

function sellStageReading(type: string, stage: string): VarPolicyOpReading {
  const known = (SNIPER_SELL_STAGES as readonly string[]).includes(stage);
  if (!known || !COVER_TYPES.has(type)) {
    return cover(type, stage, stage, 'alert', `unknown sell stage "${stage || '?'}"`);
  }
  const s = stage as SniperSellStage;
  return cover(type, s, s, 'execution', SELL_STAGE_LABEL[s], s === 'reverted');
}

function coverReading(type: string, cause: string): VarPolicyOpReading {
  if (cause === COVER_PRE_GOAL_PRICE_UNAVAILABLE) {
    // A sell DID go out — at the top of the book — but not at the price the row
    // asked for. An alert that still carries the sell side, so the feed shows
    // the order and the operator reads why it is not the pre-goal price.
    const reading = cover(
      type,
      cause,
      cause,
      'alert',
      'no pre-goal price captured — sold at the top of the book instead',
    );
    return COVER_TYPES.has(type) ? { ...reading, side: 'sell' } : reading;
  }
  if (cause === COVER_NONE_CONFIGURED) {
    return cover(
      type,
      cause,
      cause,
      'alert',
      'goal reverted and NO sell is configured on this row — the position is HELD on a dead goal; sell by hand or set a sell',
    );
  }
  const label = COVER_CAUSE_LABEL[cause];
  if (!label || !COVER_TYPES.has(type)) {
    return cover(type, cause, cause, 'alert', `unknown cover cause "${cause || '?'}"`);
  }
  const reading = cover(type, cause, cause, 'execution', label);
  return cause === 'chased' ? { ...reading, amended: true } : reading;
}

function latchReading(parsed: VarPolicyReason): VarPolicyOpReading {
  const { stage, result } = parsed;
  const reading = (
    kind: VarPolicyOpKind,
    side: 'buy' | 'sell' | null,
    what: string,
    orderIdKind: VarPolicyOrderIdKind = null,
    filledNote: string | null = null,
  ): VarPolicyOpReading => ({
    orderIdKind,
    landed: stage === STAGE_UNCONFIRMED && result === RESOLVED_LANDED && kind === 'execution',
    filledNote,
    stage,
    result,
    kind,
    side,
    label: `${LATCH_LABEL_PREFIX}${what}`,
    alert: kind === 'alert',
    executed: kind === 'execution' && result === RESOLVED_LANDED,
    amended: false,
    // The latch never says the goal is gone — that is `sell_stage:reverted`.
    goalReversed: false,
  });

  // Asked FIRST and whatever the stage: a frozen mark is not a stage's business.
  if (result === FROZEN_RESULT) {
    const held = stage === STAGE_FROZEN ? parsed.cause : stage;
    return reading(
      'alert',
      null,
      `alert: MARK FROZEN (${held || 'unknown'} held back) — an order's fate is unknown; nothing automatic will act`,
      'client',
    );
  }
  if (result === INVARIANT_REFUSED) {
    const lane = (stage === STAGE_INVARIANT ? parsed.cause : stage) || 'an order';
    return reading(
      'alert',
      null,
      `alert: engine REFUSED to place ${lane} — it would exceed the position. VERIFY THE VENUE`,
      stage === STAGE_INVARIANT ? 'collision' : null,
    );
  }
  if (stage === STAGE_UNCONFIRMED && parsed.cause === LATCHED_KIND_SELL) {
    switch (result) {
      case RESOLVED_LANDED:
        return reading('execution', 'sell', 'sell confirmed on the venue', 'venue');
      case RESOLVED_CANCELLED_UNFILLED:
        return reading(
          'info',
          null,
          'sell order was cancelled unfilled — the engine resumes',
          'venue',
        );
      case RESOLVED_CANCELLED_ON_HALT:
        return reading(
          'alert',
          null,
          'alert: sell order found resting under a HALT and cancelled',
          'venue',
          'filled before the cancel',
        );
      case HALT_CANCEL_PENDING:
      case CONFLICT_CANCEL_PENDING:
        return reading(
          'alert',
          null,
          `alert: sell order is RESTING and its cancel has NOT confirmed (${
            result === HALT_CANCEL_PENDING ? 'halt' : 'conflict'
          }) — the engine retries`,
          'venue',
          'filled so far',
        );
      case STILL_CANCEL_PENDING:
        // The reminder carries OUR client id, unlike the first sighting.
        return reading(
          'alert',
          null,
          'alert: STILL RESTING — the cancel of the sell order has not confirmed',
          'client',
        );
      case RELEASED_BY_OPERATOR:
        return reading(
          'info',
          null,
          'sell order released by operator — the engine resumes',
          'client',
        );
      case RELEASE_REFUSED_ORDER_FOUND:
        return reading(
          'alert',
          null,
          'alert: the sell order EXISTS on the venue — release refused, resolving it',
          // The one release answer that names the VENUE's id: the engine found
          // the order, so that is the id it has.
          'venue',
          'filled so far',
        );
      case RELEASE_REFUSED_READ_FAILED:
        return reading(
          'alert',
          null,
          'alert: venue read failed — still frozen, retry the release',
          'client',
        );
      case RELEASE_REFUSED_HALTED:
        return reading(
          'alert',
          null,
          'alert: halted — release refused; re-issue after the halt',
          'client',
        );
      case RELEASE_REFUSED_TOO_SOON:
        return reading(
          'alert',
          null,
          'alert: release refused — too soon; retry once the row offers Release again',
          'client',
        );
      case RELEASE_REFUSED_NO_CLEAN_READ:
        return reading(
          'alert',
          null,
          'alert: release refused — the engine cannot read Kalshi; the order can neither be found nor released; manage the position by hand',
          'client',
        );
      case RELEASE_IGNORED_MALFORMED:
        return reading('alert', null, 'alert: release command unreadable — retry', 'client');
      case RELEASE_IGNORED_STALE_COID:
        return reading('info', null, 'release ignored — the frozen order changed', 'client');
      case FIXTURE_REMOVED:
        return reading(
          'alert',
          null,
          "alert: fixture deactivated with the sell order's fate UNKNOWN — search Kalshi by the client order id",
          'client',
        );
      case RESOLVED_CONFLICT:
        return reading(
          'alert',
          null,
          'alert: a SECOND sell order was found and cancelled — VERIFY THE VENUE',
          'venue',
          'filled before the cancel',
        );
      case STILL_UNCONFIRMED:
        return reading(
          'alert',
          null,
          "alert: STILL FROZEN — the sell order's fate is unknown",
          'client',
        );
      case STILL_UNCONFIRMED_READ_FAILED:
        return reading(
          'alert',
          null,
          "alert: STILL FROZEN — the sell order's fate is unknown, and the venue read is FAILING",
          'client',
        );
      default:
    }
  }
  if (result === COVER_UNCONFIRMED) {
    return reading('alert', null, 'alert: cover reconcile unconfirmed', 'venue');
  }
  if (result === SELL_REJECTED) {
    return reading(
      'alert',
      null,
      'alert: venue REJECTED the sell order — nothing placed; the engine retries',
    );
  }
  const unconfirmed = result.endsWith(UNCONFIRMED_SUFFIX);
  return reading(
    'alert',
    null,
    `alert: ${result || 'unreadable reason'}${unconfirmed ? CHECK_VENUE : ''}`,
    unconfirmed ? 'client' : null,
  );
}
